/**
 * Mock of the K8 routes for `VITE_MOCK=1` (real routes: `audit.ts`, `coupons.ts`,
 * `campaigns.ts` tags, `contacts.ts` PATCH and POST /import). In memory only.
 */
import type {
  AuditEntry,
  CampaignTagCount,
  ContactEditInput,
  CouponOverview,
  CsvImportReport,
  CsvInviteResult,
  CsvInviteStatus,
  Role,
} from '../../types/api';

function json(body: unknown, status = 200): Response {
  return new Response(JSON.stringify(body), {
    status,
    headers: { 'content-type': 'application/json' },
  });
}

const TAG_PATTERN = /^[a-z0-9æøå-]{1,40}$/;

let tagsByCampaign = new Map<string, string[]>();
let contactEdits = new Map<string, ContactEditInput>();
let contactFields = new Map<string, Record<string, string>>();
let importedEmails = new Set<string>();
let invitableByImport = new Map<string, number>();
let invitesEnabled = false;

export function resetMockK8(): void {
  tagsByCampaign = new Map();
  contactEdits = new Map();
  contactFields = new Map();
  importedEmails = new Set();
  invitableByImport = new Map();
  invitesEnabled = false;
}

/** Test seam: the invite feature is off by default, like the real one. */
export function setMockInvitesEnabled(on: boolean): void {
  invitesEnabled = on;
}

export function mockCampaignTags(campaignId: string): string[] {
  return tagsByCampaign.get(campaignId) ?? [];
}

export function mockContactEdit(contactId: string): ContactEditInput | undefined {
  return contactEdits.get(contactId);
}

export function mockContactFields(contactId: string): Record<string, string> {
  return contactFields.get(contactId) ?? { vat_number: 'DE123' };
}

const AUDIT: AuditEntry[] = [
  ['a9', 'anna@example.com', 'contact.import', 'contact', null, { created: 12, updated: 3 }],
  ['a8', 'anna@example.com', 'contact.edit', 'contact', 'c-1', { changed: ['firstName'] }],
  ['a7', 'bjorn@example.com', 'automation.enable', 'automation', 'welcome', {}],
  ['a6', 'anna@example.com', 'campaign.tags', 'campaign', 'c-1', { tags: ['launch'] }],
  ['a5', 'bjorn@example.com', 'user.invite', 'user', null, {}],
].map(([id, email, action, targetType, targetId, details], i) => ({
  id: id as string,
  userId: email === 'anna@example.com' ? 'u-anna' : 'u-bjorn',
  userEmail: email as string,
  userName: null,
  action: action as string,
  targetType: targetType as string,
  targetId: targetId as string | null,
  details: details as Record<string, unknown>,
  createdAt: 1_760_000_000_000 - i * 3_600_000,
}));

const COUPONS: CouponOverview = {
  personal: [
    {
      campaignId: 'c-1',
      name: 'Black Friday',
      status: 'sent',
      percent: 10,
      prefix: 'NP',
      issued: 1200,
      redeemed: 87,
    },
  ],
  automationIssued: 45,
  shared: [
    { code: 'summer', orders: 14, discounts: [{ currency: 'EUR', amount: 182.5 }] },
    {
      code: 'friday24',
      orders: 9,
      discounts: [
        { currency: 'EUR', amount: 96.4 },
        { currency: 'DKK', amount: 120 },
      ],
    },
    { code: 'freeship', orders: 3, discounts: [] },
  ],
  ordersCapped: false,
  redeemedCapped: false,
};

/** Returns a response for a K8 path, or `null` when the path is not one. */
export function handleMockK8(
  method: string,
  pathname: string,
  params: URLSearchParams,
  body: unknown,
  role: Role,
  windowStepUp: (b: { totpCode?: string; recoveryCode?: string }) => Response | null,
): Response | null {
  if (pathname === '/audit' && method === 'GET') {
    if (role !== 'admin') return json({ error: 'forbidden' }, 403);
    const action = params.get('action');
    const userId = params.get('userId');
    const before = params.get('before');
    const limit = Number(params.get('limit') ?? 2);
    let rows = AUDIT.filter(
      (e) => (!action || e.action.startsWith(action)) && (!userId || e.userId === userId),
    );
    if (before) {
      const at = rows.findIndex((e) => e.id === before);
      rows = at >= 0 ? rows.slice(at + 1) : rows;
    }
    const page = rows.slice(0, limit);
    const last = page[page.length - 1];
    return json({
      entries: page,
      nextCursor: rows.length > limit && last ? last.id : null,
    });
  }

  if (pathname === '/coupons' && method === 'GET') return json(COUPONS);

  if (pathname === '/campaigns/tags' && method === 'GET') {
    const counts = new Map<string, number>();
    for (const tags of tagsByCampaign.values()) {
      for (const t of tags) counts.set(t, (counts.get(t) ?? 0) + 1);
    }
    const tags: CampaignTagCount[] = [...counts.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([tag, count]) => ({ tag, count }));
    return json({ tags });
  }

  const tagsMatch = /^\/campaigns\/([^/]+)\/tags$/.exec(pathname);
  if (tagsMatch && method === 'PUT') {
    if (role === 'viewer') return json({ error: 'forbidden' }, 403);
    const raw = ((body ?? {}) as { tags?: unknown }).tags;
    if (!Array.isArray(raw)) return json({ error: 'invalid_request' }, 400);
    const tags = [...new Set(raw.map((t) => String(t).trim().toLowerCase()))].sort();
    if (tags.length > 10) {
      return json({ error: 'invalid_request', message: 'At most 10 tags per campaign.' }, 400);
    }
    const bad = tags.find((t) => !TAG_PATTERN.test(t));
    if (bad !== undefined) {
      return json(
        {
          error: 'invalid_request',
          message: `Tag "${bad}" must be lowercase letters, digits, æøå or hyphens.`,
        },
        400,
      );
    }
    tagsByCampaign.set(tagsMatch[1] as string, tags);
    return json({ ok: true, tags });
  }

  const editMatch = /^\/contacts\/([^/]+)$/.exec(pathname);
  if (editMatch && method === 'PATCH') {
    if (role === 'viewer') return json({ error: 'forbidden' }, 403);
    const id = editMatch[1] as string;
    const b = (body ?? {}) as ContactEditInput;
    if ('consent' in b || 'status' in b) return json({ error: 'invalid_request' }, 400);
    const changed: string[] = [];
    const prev = contactEdits.get(id) ?? {};
    for (const key of ['firstName', 'lastName', 'language', 'country'] as const) {
      if (b[key] !== undefined) changed.push(key);
    }
    if (b.customFields) {
      changed.push('customFields');
      const next = { ...mockContactFields(id) };
      for (const [k, v] of Object.entries(b.customFields)) {
        if (v === null) delete next[k];
        else next[k] = v;
      }
      contactFields.set(id, next);
    }
    contactEdits.set(id, { ...prev, ...b, customFields: undefined });
    return json({ ok: true, changed });
  }

  if (pathname === '/contacts/import' && method === 'POST') {
    if (role !== 'admin') return json({ error: 'forbidden' }, 403);
    const b = (body ?? {}) as {
      rows?: { email: string }[];
      consent?: { mode: string; importId?: string };
      dryRun?: boolean;
      totpCode?: string;
      recoveryCode?: string;
    };
    if (!Array.isArray(b.rows) || b.rows.length > 50 || !b.consent) {
      return json({ error: 'invalid_request' }, 400);
    }
    if (!b.dryRun) {
      const failure = windowStepUp(b);
      if (failure) return failure;
    }
    const report: CsvImportReport = {
      received: b.rows.length,
      created: 0,
      updated: 0,
      unchanged: 0,
      skippedInvalidEmail: 0,
      skippedSuppressed: 0,
      skippedDuplicateInChunk: 0,
      invitable: 0,
      notMailable: 0,
      labelsApplied: 0,
      labelsRefused: 0,
      languagesRefused: 0,
      countriesRefused: 0,
    };
    const seen = new Set<string>();
    for (const row of b.rows) {
      const email = row.email.trim().toLowerCase();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        report.skippedInvalidEmail += 1;
      } else if (seen.has(email)) {
        report.skippedDuplicateInChunk += 1;
      } else if (importedEmails.has(email)) {
        seen.add(email);
        report.unchanged += 1;
      } else {
        seen.add(email);
        report.created += 1;
        report.notMailable += 1;
        if (b.consent.mode === 'invite') report.invitable += 1;
        if (!b.dryRun) importedEmails.add(email);
      }
    }
    if (!b.dryRun && b.consent.mode === 'invite' && b.consent.importId) {
      invitableByImport.set(
        b.consent.importId,
        (invitableByImport.get(b.consent.importId) ?? 0) + report.invitable,
      );
    }
    return json({ ok: true, dryRun: Boolean(b.dryRun), report });
  }

  if (pathname === '/contacts/import/invite/status' && method === 'GET') {
    if (role !== 'admin') return json({ error: 'forbidden' }, 403);
    const importId = params.get('importId') ?? '';
    const status: CsvInviteStatus = {
      enabled: invitesEnabled,
      counts: {
        invitable: invitableByImport.get(importId) ?? 0,
        sending: 0,
        invited: 0,
        failed: 0,
        skipped: 0,
      },
    };
    return json(status);
  }

  if (pathname === '/contacts/import/invite' && method === 'POST') {
    if (role !== 'admin') return json({ error: 'forbidden' }, 403);
    if (!invitesEnabled) return json({ error: 'invites_disabled' }, 403);
    const b = (body ?? {}) as { importId?: string; totpCode?: string; recoveryCode?: string };
    const failure = windowStepUp(b);
    if (failure) return failure;
    const left = invitableByImport.get(b.importId ?? '') ?? 0;
    const sent = Math.min(left, 20);
    invitableByImport.set(b.importId ?? '', left - sent);
    const result: CsvInviteResult = {
      ok: true,
      sent,
      failed: 0,
      skipped: 0,
      skippedBy: {},
      remaining: left - sent,
    };
    return json(result);
  }

  return null;
}
